import React from 'react';
import { useOrganization } from '../context/OrganizationContext';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../utils/permissions';
import { Box, Alert, Typography, Container } from '@mui/material';
import TagManager from '../components/team/TagManager';
import { ledeSx } from '../theme/styleHelpers';

/**
 * Tags page - shows the tag manager for the currently selected team
 * Only accessible when a team is selected and user has appropriate permissions
 */
const Tags: React.FC = () => {
  const { currentTeamId, currentOrganizationId } = useOrganization();
  const { hasPermission } = usePermissions();

  // Check if user has permission to manage tags
  // Tags are content-management related, so check for content:create permission
  const canManageTags = hasPermission(PERMISSIONS.CONTENT_CREATE);

  // No team selected
  if (!currentTeamId || !currentOrganizationId) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="info">
          Please select a team to manage tags.
        </Alert>
      </Container>
    );
  }

  // No permission
  if (!canManageTags) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="warning">
          You don't have permission to manage tags. You need content creation permissions to access this feature.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h1" sx={{
          fontSize: 32,
          fontWeight: 800,
          letterSpacing: '-0.025em',
          lineHeight: 1.15,
          marginBottom: 1,
        }}>
          Tag Manager
        </Typography>
        <Typography sx={ledeSx}>
          Organize content with tag groups and individual tags.
        </Typography>
      </Box>
      <TagManager
        teamId={currentTeamId}
        organizationId={currentOrganizationId}
      />
    </Container>
  );
};

export default Tags;
